import { PropsWithChildren } from 'react';
import useUser from '@vl-core/hooks/useUser';

/**
 * Renders children only if the current user's role code matches one of the supplied prop roles (either "admin",
 * "hbsadmin" or "audit" or "referrer"). Note that the role codes are case insensitive.
 *
 * @param children
 * @param roles (case insensitive) some combination of audit | hbsadmin | admin | referrer
 * @returns {null|*}
 * @constructor
 */

export const ROLE_CODES = [
  'admin',
  'super',
  'audit',
  'referrer',
  'conf',
  'hbsadmin',
  'custserv',
  'reporting',
  'clinician',
  'clinlead',
  'clinaudit',
  'patient',
  'ops',
  'opshbs'
] as const;

export type RoleCode = (typeof ROLE_CODES)[number];

type DisplayForRoleCodeProps = PropsWithChildren<{
  [key in RoleCode]?: boolean;
}>;

export function DisplayForRoleCode({ children, ...role_codes }: DisplayForRoleCodeProps) {
  const user = useUser();
  const userRoleCode = user.user_role_code?.toLowerCase?.();

  for (const role of Object.keys(role_codes) as RoleCode[]) {
    if (role_codes[role] && role === userRoleCode) {
      return children;
    }

    if (!ROLE_CODES.includes(role)) {
      throw Error(`Bad role specified for "DisplayForRoles": "${role}"`);
    }
  }

  return null;
}

export default DisplayForRoleCode;
